import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listResource, getResource, createResource, updateResource } from '@diggens/api-client';
import type { MetricDefinition } from '@diggens/types';

/**
 * Get metric definitions for a fund, optionally including portfolio company-specific metrics.
 *
 * @param fundId - The fund ID to get metrics for
 * @param portfolioCompanyId - Optional portfolio company ID to include company-specific metrics
 * @returns Query result with fund-level + company-specific metrics (if portfolioCompanyId provided)
 */
export function useMetricDefinitions(fundId: string, portfolioCompanyId?: string | null) {
  return useQuery({
    queryKey: ['metricDefinitions', fundId, portfolioCompanyId ?? null],
    queryFn: async () => {
      const params: Record<string, string | undefined> = {
        fund_id: fundId,
        portfolio_company_id: portfolioCompanyId ?? undefined,
      };
      const { data } = await listResource<MetricDefinition>('metric_definitions', params);
      return data;
    },
    enabled: !!fundId,
  });
}

export function useMetricDefinition(definitionId: string) {
  return useQuery({
    queryKey: ['metricDefinitions', definitionId],
    queryFn: () => getResource<MetricDefinition>('metric_definitions', definitionId),
    enabled: !!definitionId,
  });
}

export function useCreateMetricDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      tenant_id: string;
      fund_id: string;
      portfolio_company_id?: string | null; // NULL = fund-level, UUID = company-specific
      name: string;
      description?: string | null;
      data_type: 'number' | 'currency' | 'percentage' | 'text';
      is_required: boolean;
      display_order: number;
    }) => createResource<MetricDefinition>('metric_definitions', input),
    onSuccess: (data) => {
      // Invalidate fund-level queries
      queryClient.invalidateQueries({ queryKey: ['metricDefinitions', data.fund_id] });
      // If company-specific, also invalidate company-specific queries
      if (data.portfolio_company_id) {
        queryClient.invalidateQueries({
          queryKey: ['metricDefinitions', data.fund_id, data.portfolio_company_id],
        });
      }
    },
  });
}

export function useUpdateMetricDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { definitionId: string; updates: Partial<MetricDefinition> }) =>
      updateResource<MetricDefinition>('metric_definitions', variables.definitionId, variables.updates),
    onSuccess: (data) => {
      // Invalidate fund-level queries
      queryClient.invalidateQueries({ queryKey: ['metricDefinitions', data.fund_id] });
      queryClient.invalidateQueries({ queryKey: ['metricDefinitions', data.id] });
      // If company-specific, also invalidate company-specific queries
      if (data.portfolio_company_id) {
        queryClient.invalidateQueries({
          queryKey: ['metricDefinitions', data.fund_id, data.portfolio_company_id],
        });
      }
    },
  });
}

export function useArchiveMetricDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { definitionId: string; fundId: string; portfolioCompanyId?: string | null }) =>
      updateResource<MetricDefinition>('metric_definitions', variables.definitionId, { archived_at: new Date().toISOString() }),
    onSuccess: (_, variables) => {
      // Invalidate fund-level queries
      queryClient.invalidateQueries({ queryKey: ['metricDefinitions', variables.fundId] });
      queryClient.invalidateQueries({ queryKey: ['metricDefinitions', variables.definitionId] });
      // If company-specific, also invalidate company-specific queries
      if (variables.portfolioCompanyId) {
        queryClient.invalidateQueries({
          queryKey: ['metricDefinitions', variables.fundId, variables.portfolioCompanyId],
        });
      }
    },
  });
}
